/**
 * /academy/mocks — the numbered mock exams (Examen blanc n° 1, 2, …) with the official structure
 * of the TCF Canada read from the format table; start one → the player.
 */

import {
  ApiError,
  getAcademyAttemptsListQueryKey,
  getAcademyMocksListQueryKey,
  useAcademyFreeMockRetrieve, useAcademyFormatRetrieve,
  useAcademyMeRetrieve,
  useAcademyMocksCreate,
  useAcademyMocksList,
} from "@agencyos/api-client";
import { asFreeMockContent } from "@agencyos/core";
import { Badge, Button, GlassCard, Skeleton } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ClipboardList, Clock, Play, RotateCcw } from "lucide-react";
import { type CSSProperties, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { bandText } from "./ielts/band";
import { IELTS, estimateKey, examParams, useActiveExam } from "./ielts/exam";
import { parseBest, parseStructure } from "./progressTypes";
import { SKILLS, isObject } from "./types";
import { PageHead, SkillIcon } from "./ui";

/** The four sections of the exam, as read from the format table (counts and minutes). */
export function ExamStructure({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const { exam } = useActiveExam();
  const skillKey = exam === IELTS ? "ielts.skill" : exam === "tef_canada" ? "tef.skill" : "academy.skill";
  const format = useAcademyFormatRetrieve(examParams(exam), { query: { staleTime: 3_600_000, retry: false } });
  const structure = useMemo(
    () => parseStructure(format.data?.status === 200 ? format.data.data.sections : undefined),
    [format.data],
  );
  const shown = SKILLS.filter((s) => structure[s]);
  if (!shown.length) return null;
  return (
    <ol className={`ac-structure${compact ? " ac-structure--compact" : ""}`} aria-label={t("academy.mocks.structure")}>
      {shown.map((skill, index) => {
        const row = structure[skill];
        if (!row) return null;
        const mcq = skill === "co" || skill === "ce";
        return (
          <li key={skill} className={`ac-structure__item ac-skill--${skill}`}>
            <SkillIcon skill={skill} size={16} />
            <span className="ac-structure__text">
              <strong>
                <span className="ac-structure__step" aria-hidden="true">
                  {index + 1}
                </span>
                {t(`${skillKey}.${skill}`)}
              </strong>
              <span className="ac-structure__facts">
                <span>{t(mcq ? "academy.mocks.questions" : "academy.mocks.tasks", { count: row.count })}</span>
                <span className="ac-structure__time">
                  <Clock aria-hidden size={13} /> {t("academy.mocks.minutes", { count: row.minutes })}
                </span>
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function MocksPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { exam } = useActiveExam();
  const ielts = exam === IELTS;
  const me = useAcademyMeRetrieve({ query: { retry: false } });
  const mocks = useAcademyMocksList(examParams(exam), { query: { retry: false } });
  const create = useAcademyMocksCreate();
  const [starting, setStarting] = useState<string | null>(null);
  const rows = mocks.data?.status === 200 ? mocks.data.data : [];
  const locked = me.data?.status === 200 ? (exam !== "tcf_canada" ? me.data.data.access[exam] !== true : !me.data.data.has_access) : false;
  // D-172: without a plan, only the free mock of the account opens (the server decides)
  const freeMock = useAcademyFreeMockRetrieve({ exam }, { query: { retry: false, enabled: locked } });
  const free = freeMock.data?.status === 200 ? freeMock.data.data : undefined;
  const freeId = free && (free.state === "available" || free.state === "in_progress") ? (asFreeMockContent(free.mock)?.id ?? null) : null;
  const error = create.error instanceof ApiError ? (create.error.detail ?? t("common.error")) : create.error ? t("common.error") : null;
  const doneCount = rows.filter((m) => m.done > 0).length;
  const nextMock = rows.find((m) => m.done === 0)?.id;

  const start = (mock: string) => {
    setStarting(mock);
    create.mutate(
      { data: { mock } },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          void queryClient.invalidateQueries({ queryKey: getAcademyAttemptsListQueryKey() });
          void queryClient.invalidateQueries({ queryKey: getAcademyMocksListQueryKey(examParams(exam)) });
          navigate(`/academy/attempt/${response.data.id}`);
        },
        onSettled: () => setStarting(null),
      },
    );
  };

  return (
    <div className="ac-page">
      <PageHead
        eyebrow={exam !== "tcf_canada" ? `${t(`ielts.exams.${exam}.name`)} · ${t("academy.mocks.eyebrow")}` : t("academy.mocks.eyebrow")}
        title={t("academy.mocks.title")}
        subtitle={<p>{ielts ? t("ielts.mocks.intro") : exam === "tef_canada" ? t("tef.mocks.intro") : t("academy.mocks.intro")}</p>}
        aside={
          rows.length > 0 ? (
            <p className="ac-head-stat">
              <span className="ac-head-stat__value ao-num">
                {doneCount}/{rows.length}
              </span>
              <span className="ac-head-stat__label">{t("academy.ui.mocks.doneCount")}</span>
            </p>
          ) : undefined
        }
      />

      <GlassCard as="section" className="ac-card" aria-labelledby="academy-structure">
        <div className="ac-card__head">
          <h2 id="academy-structure" className="ac-card-title">
            <span className="ac-pastille ac-tone--accent" aria-hidden="true">
              <ClipboardList size={16} />
            </span>
            <span>{t("academy.mocks.structure")}</span>
          </h2>
        </div>
        <ExamStructure />
        <p className="ac-footnote">{t("academy.mocks.structureHint")}</p>
      </GlassCard>

      {locked && freeId ? (
        <p className="ac-note" role="note">
          {t("coaching.free.left", { count: 1 })}
        </p>
      ) : null}
      {locked && !freeId && (
        <p className="ac-note" role="note">
          {t("academy.train.locked")}
        </p>
      )}
      {error && (
        <p className="ko" role="alert">
          {error}
        </p>
      )}

      {mocks.isLoading && (
        <div className="ac-mocks">
          <Skeleton style={{ height: 220 }} />
          <Skeleton style={{ height: 220 }} />
        </div>
      )}
      {mocks.isSuccess && rows.length === 0 && (
        <div className="ac-surface ac-empty">
          <ClipboardList aria-hidden size={22} />
          <p className="ac-secondary">{t("academy.mocks.empty")}</p>
        </div>
      )}
      {rows.length > 0 && (
        <ul className="ac-mocks">
          {rows.map((mock, index) => {
            const best = ielts ? null : parseBest(mock.best);
            const ieltsBest = ielts && isObject(mock.best) ? mock.best : null;
            const bands = ieltsBest && isObject(ieltsBest.bands) ? ieltsBest.bands : {};
            return (
              <li key={mock.id} className="ao-enter" style={{ "--ao-i": index } as CSSProperties}>
                <GlassCard as="article" className="ac-card ac-mock" aria-labelledby={`mock-${mock.id}`}>
                  <div className="ac-mock__top">
                    <span className="ac-mock__number ao-num" aria-hidden="true">
                      {String(mock.number).padStart(2, "0")}
                    </span>
                    {mock.done > 0 ? (
                      <Badge tone="success">
                        <CheckCircle2 aria-hidden size={12} /> {t("academy.mocks.done", { count: mock.done })}
                      </Badge>
                    ) : (
                      <Badge>{t("academy.mocks.notDone")}</Badge>
                    )}
                  </div>
                  <p className="ac-eyebrow">{t("academy.mocks.number", { n: mock.number })}</p>
                  <h2 id={`mock-${mock.id}`} className="ac-mock__title">
                    {mock.title}
                  </h2>
                  {ieltsBest && (
                    <div className="ac-mock__best">
                      <p className="ac-caption">{t("academy.mocks.best")}</p>
                      <p className="ac-mock__best-values ao-num">
                        {t("ielts.mocks.bestOverall", { band: bandText(typeof ieltsBest.overall === "number" ? ieltsBest.overall : null) })}
                        {" · "}
                        {SKILLS.filter((s) => typeof bands[s] === "number")
                          .map((s) => `${t(`ielts.skillShort.${s}`)} ${bandText(bands[s] as number)}`)
                          .join(" · ")}
                      </p>
                      {typeof ieltsBest.attempt === "string" && (
                        <Link className="ac-more" to={`/academy/attempt/${ieltsBest.attempt}/results`}>
                          {t("academy.history.see")}
                        </Link>
                      )}
                    </div>
                  )}
                  {mock.session_theme && <p className="ac-footnote">{t("academy.mocks.theme", { theme: mock.session_theme })}</p>}
                  {best && (
                    <div className="ac-mock__best">
                      <p className="ac-caption">{t("academy.mocks.best")}</p>
                      <p className="ac-mock__best-values ao-num">
                        {SKILLS.filter((s) => best.nclc[s] !== undefined)
                          .map((s) => `${t(`academy.skillShort.${s}`)} ${best.nclc[s] ?? 0}`)
                          .join(" · ")}
                      </p>
                      {best.attempt && (
                        <Link className="ac-more" to={`/academy/attempt/${best.attempt}/results`}>
                          {t("academy.history.see")}
                        </Link>
                      )}
                    </div>
                  )}
                  <div className="ac-mock__foot">
                    <Button
                      block
                      variant={mock.id === nextMock ? "primary" : "secondary"}
                      icon={mock.done > 0 ? <RotateCcw aria-hidden size={16} /> : <Play aria-hidden size={16} />}
                      disabled={(locked && mock.id !== freeId) || create.isPending}
                      onClick={() => start(mock.id)}
                      aria-label={t("academy.mocks.startNamed", { n: mock.number })}
                    >
                      {starting === mock.id ? t("common.loading") : mock.done > 0 ? t("academy.mocks.again") : t("academy.mocks.start")}
                    </Button>
                  </div>
                </GlassCard>
              </li>
            );
          })}
        </ul>
      )}
      <p className="ac-footnote">{t(estimateKey(exam))}</p>
    </div>
  );
}
